import { toast } from "sonner";
import { fetchWsTicket } from "@/services/auth.service";
import {
  buildSocketUrl,
  MAX_RECONNECT_ATTEMPTS,
  nextReconnectDelay,
  shouldReconnectOnClose,
} from "./compass-queue.utils";

export interface QueueSocketCallbacks {
  onOpen: () => void;
  onClose: () => void;
  onMessage: (event: MessageEvent) => void;
  onTerminalClose: () => void;
  /** Every reconnect attempt has been spent; the socket will not come back. */
  onReconnectExhausted: () => void;
}

export interface QueueSocketHandle {
  dispose: () => void;
}

interface SocketSession {
  disposed: boolean;
  attempt: number;
  socket: WebSocket | null;
  retryTimeout: ReturnType<typeof setTimeout> | null;
  didToastTicket: boolean;
  didExhaust: boolean;
}

async function mintTicketOrNotify(session: SocketSession): Promise<string | null> {
  try {
    return await fetchWsTicket();
  } catch {
    if (!session.disposed && !session.didToastTicket) {
      session.didToastTicket = true;
      toast.error("Could not connect to Compass updates");
    }
    return null;
  }
}

function attachSocketHandlers(
  socket: WebSocket,
  callbacks: QueueSocketCallbacks,
  scheduleReconnect: () => void,
  session: SocketSession
): void {
  socket.onopen = () => {
    if (session.disposed) return;
    session.attempt = 0;
    session.didToastTicket = false;
    callbacks.onOpen();
  };
  socket.onmessage = (event: MessageEvent) => {
    if (!session.disposed) callbacks.onMessage(event);
  };
  socket.onerror = () => {
    if (!session.disposed) callbacks.onClose();
  };
  socket.onclose = (event: CloseEvent) => {
    if (session.disposed) return;
    callbacks.onClose();
    if (!shouldReconnectOnClose(event.code)) {
      callbacks.onTerminalClose();
      return;
    }
    scheduleReconnect();
  };
}

function scheduleReconnect(
  session: SocketSession,
  connect: () => void,
  onExhausted: () => void
): void {
  if (session.disposed) return;
  // Giving up silently leaves isConnected false with no toast and no way back,
  // unlike the terminal-close path. Say so once, then stop.
  if (session.attempt >= MAX_RECONNECT_ATTEMPTS) {
    if (!session.didExhaust) {
      session.didExhaust = true;
      onExhausted();
    }
    return;
  }
  const delay = nextReconnectDelay(session.attempt);
  session.attempt += 1;
  session.retryTimeout = setTimeout(() => {
    session.retryTimeout = null;
    connect();
  }, delay);
}

async function connectQueueSocket(
  docId: string,
  callbacks: QueueSocketCallbacks,
  session: SocketSession
): Promise<void> {
  if (session.disposed) return;
  const ticket = await mintTicketOrNotify(session);
  if (session.disposed) return;
  if (ticket == null) {
    scheduleReconnect(
      session,
      () => {
        void connectQueueSocket(docId, callbacks, session);
      },
      callbacks.onReconnectExhausted
    );
    return;
  }
  session.socket = new WebSocket(buildSocketUrl(ticket, docId));
  attachSocketHandlers(
    session.socket,
    callbacks,
    () => {
      scheduleReconnect(
        session,
        () => {
          void connectQueueSocket(docId, callbacks, session);
        },
        callbacks.onReconnectExhausted
      );
    },
    session
  );
}

export function startQueueSocket(
  docId: string,
  callbacks: QueueSocketCallbacks
): QueueSocketHandle {
  const session: SocketSession = {
    disposed: false,
    attempt: 0,
    socket: null,
    retryTimeout: null,
    didToastTicket: false,
    didExhaust: false,
  };

  void connectQueueSocket(docId, callbacks, session);

  return {
    dispose: () => {
      session.disposed = true;
      if (session.retryTimeout != null) clearTimeout(session.retryTimeout);
      session.socket?.close();
      session.socket = null;
    },
  };
}
